import * as THREE from "three";
import { GRASS_RANGE } from "@ghostland/shared";
import { dayNight } from "./dayNight.js";

/**
 * Cuttable grass tufts in the city (the grass-cutting job). Subtle, low-res, hand-drawn pixel
 * tufts in muted greens — three variants — that sway gently in the wind (each tilts around its
 * base on its own sine phase). Server-authoritative: nodes are added/removed from `grassNodes`
 * state; this just renders them, animates the sway, and reports the nearest one for the E prompt.
 */
const U = 1 / 32; // world units per server px

// Muted, desaturated greens (dark base → light tip) — deliberately subtle, not bright.
const PAL = ["#39512f", "#45633a", "#547746"];

interface Blade { x: number; h: number; bend: number; wide?: boolean }
/** Three small tuft variants, each a few blades sprouting from the base of a 16px cell. */
const VARIANTS: Blade[][] = [
  [{ x: 6, h: 9, bend: -2 }, { x: 8, h: 12, bend: 0, wide: true }, { x: 10, h: 8, bend: 2 }],
  [{ x: 4, h: 8, bend: -3 }, { x: 6, h: 11, bend: -1 }, { x: 8, h: 13, bend: 0, wide: true }, { x: 10, h: 10, bend: 1 }, { x: 12, h: 8, bend: 3 }],
  [{ x: 7, h: 12, bend: -1 }, { x: 8, h: 14, bend: 0, wide: true }, { x: 9, h: 11, bend: 2 }],
];

const S = 16;
function drawGrass(variant: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = S; c.height = S;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  for (const b of VARIANTS[variant % VARIANTS.length]) {
    for (let i = 0; i < b.h; i++) {
      const y = S - 1 - i;
      const x = b.x + Math.round((i / b.h) * b.bend); // blades curve as they rise
      ctx.fillStyle = i >= b.h - 2 ? PAL[2] : (i % 2 ? PAL[1] : PAL[0]); // lighter at the tip
      ctx.fillRect(x, y, 1, 1);
      if (b.wide && i < b.h - 2) ctx.fillRect(x - (b.bend < 0 ? -1 : 1), y, 1, 1);
    }
  }
  return c;
}

const texCache: (THREE.CanvasTexture | undefined)[] = [];
function grassTex(variant: number): THREE.CanvasTexture {
  const v = variant % VARIANTS.length;
  if (!texCache[v]) {
    const t = new THREE.CanvasTexture(drawGrass(v));
    t.magFilter = t.minFilter = THREE.NearestFilter;
    t.colorSpace = THREE.SRGBColorSpace;
    texCache[v] = t;
  }
  return texCache[v]!;
}

/** The cuttable-grass tuft texture (one of `GRASS_TUFT_VARIANTS` variants), shared/cached. Reused as
 *  decorative plants elsewhere (e.g. the Kapon meadow) so they match the grass-cutter grass exactly. */
export function grassTuftTexture(variant: number): THREE.CanvasTexture { return grassTex(variant); }
export const GRASS_TUFT_VARIANTS = VARIANTS.length;

interface GrassEntry { sprite: THREE.Sprite; mat: THREE.SpriteMaterial; x: number; y: number; phase: number; speed: number; amp: number }

export class GrassController {
  private readonly nodes = new Map<string, GrassEntry>();
  private windT = 0;
  private visible = false;

  constructor(private readonly scene: THREE.Scene) {}

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    for (const e of this.nodes.values()) e.sprite.visible = v;
  }

  addNode(key: string, node: { variant: number; x: number; y: number }): void {
    const mat = new THREE.SpriteMaterial({ map: grassTex(node.variant), transparent: true, alphaTest: 0.4 });
    const sprite = new THREE.Sprite(mat);
    sprite.center.set(0.5, 0); // base-anchored: sways around its root
    const h = 0.5;
    sprite.scale.set(h, h, 1);
    sprite.position.set(node.x * U, 0.02, node.y * U);
    sprite.visible = this.visible;
    dayNight.registerMat(mat, sprite.position); // grass darkens/cools at night + lights near lamps
    this.scene.add(sprite);
    this.nodes.set(key, {
      sprite, mat, x: node.x, y: node.y,
      phase: Math.random() * Math.PI * 2, speed: 0.9 + Math.random() * 0.7, amp: 0.07 + Math.random() * 0.07,
    });
  }

  removeNode(key: string): void {
    const e = this.nodes.get(key);
    if (!e) return;
    dayNight.unregisterMat(e.mat);
    this.scene.remove(e.sprite);
    e.mat.dispose();
    this.nodes.delete(key);
  }

  /** Nearest tuft within GRASS_RANGE of the player (server px), or null. */
  nearest(px: number, py: number): string | null {
    let best: string | null = null, bestD = GRASS_RANGE * GRASS_RANGE;
    for (const [key, e] of this.nodes) {
      const dx = e.x - px, dy = e.y - py, d = dx * dx + dy * dy;
      if (d <= bestD) { bestD = d; best = key; }
    }
    return best;
  }

  /** Advance the wind sway (dt in seconds). */
  tick(dt: number): void {
    this.windT += dt;
    for (const e of this.nodes.values()) e.mat.rotation = Math.sin(this.windT * e.speed + e.phase) * e.amp;
  }

  dispose(): void {
    for (const k of [...this.nodes.keys()]) this.removeNode(k);
  }
}
